import { useEffect, useState } from "react";
import Select, { components } from "react-select";
import config from "../../config";
import axios from "../../utils/Security/axiosInstance";

type ApiDropdownProps<T> = {
  api: string;
  labelKey: keyof T;
  valueKey: keyof T;
  onSelect?: (item: T | T[] | undefined) => void;
  label?: string;
  value?: string | number | Array<string | number>;
  onChange?: (val: string | number | Array<string | number>) => void;
  options?: T[];
  required?: boolean;
  onOpen?: () => void;
  disabled?: boolean;
  isMulti?: boolean;
};

function ApiDropdown<T extends Record<string, any>>({
  api,
  labelKey,
  valueKey,
  onSelect,
  label,
  value,
  onChange,
  options,
  required,
  onOpen,
  isMulti = false,
  disabled = false,
}: ApiDropdownProps<T>) {
  const [fetchedOptions, setFetchedOptions] = useState<T[]>([]);
  const isForTelebras = localStorage.getItem("isForTelebras") === "true";
  const isForPetrobras = localStorage.getItem("isForPetrobras") === "true";

  // Fetch options from API if not provided
  useEffect(() => {
    if (!options && api) {
      axios
        .get<T[]>(`${config.apiBaseUrl}/api/${api}`)
        .then((response) => {
          let data = response.data;

          if (api === "roles" && isForTelebras) {
            data = data.filter(
              (item: any) =>
                item.roleName === "Master" || item.roleName === "Gilat",
            );
          }

          if (api === "roles" && isForPetrobras) {
            data = data.filter(
              (item: any) =>
                item.roleName === "Mareste",
            );
          }

          setFetchedOptions(data);
        })
        .catch(() => setFetchedOptions([]));
    }
  }, [api, options]);

  const finalOptions = options?.length ? options : fetchedOptions;

  // Map options to react-select format
  const customOptions = finalOptions.map((item) => ({
    label: String(item[labelKey]),
    value: String(item[valueKey]),
    data: item,
  }));

  // Only add "All" option if there are real options
  const selectAllOption = { label: "All", value: "*", data: null };
  const fullOptions =
    isMulti && customOptions.length
      ? [selectAllOption, ...customOptions]
      : customOptions;

  const handleChange = (selected: any, actionMeta?: any) => {
    const dropdownName = label || api;

    if (isMulti) {
      // Only treat "All" as select-all when the user actually picked it. When all
      // options are selected, "All" is shown as a chip too, so removing any other
      // chip would otherwise still contain "*" and re-select everything.
      const isSelectAll =
        actionMeta?.action === "select-option" &&
        actionMeta?.option?.value === "*";
      const removedAll =
        (actionMeta?.action === "remove-value" ||
          actionMeta?.action === "pop-value" ||
          actionMeta?.action === "deselect-option") &&
        (actionMeta?.removedValue ?? actionMeta?.option)?.value === "*";

      if (removedAll) {
        selected = [];
      } else {
        selected = selected?.filter((opt: any) => opt.value !== "*");
      }

      if (isSelectAll) {
        if (dropdownName === "Site") localStorage.setItem("isAllSite", "true");
        if (dropdownName === "Networks")
          localStorage.setItem("isAllNetwork", "true");
        if (dropdownName === "Link") localStorage.setItem("isAllLink", "true");

        onSelect?.(finalOptions);
        onChange?.(customOptions.map((opt) => opt.value));
      } else {
        const selectedItems = selected?.map((opt: any) => opt.data);
        const selectedValues = selected?.map((opt: any) => opt.value);

        if (dropdownName === "Site") localStorage.setItem("isAllSite", "false");
        if (dropdownName === "Networks")
          localStorage.setItem("isAllNetwork", "false");
        if (dropdownName === "Link") localStorage.setItem("isAllLink", "false");

        onSelect?.(selectedItems);
        onChange?.(selectedValues);
      }
    } else {
      if (dropdownName === "Site") localStorage.setItem("isAllSite", "false");
      if (dropdownName === "Networks")
        localStorage.setItem("isAllNetwork", "false");
      if (dropdownName === "Link") localStorage.setItem("isAllLink", "false");

      onSelect?.(selected?.data);
      onChange?.(selected?.value);
    }
  };

  const getValue = () => {
    if (isMulti && Array.isArray(value)) {
      const selected = customOptions.filter((opt) => value.includes(opt.value));
      if (customOptions.length && selected.length === customOptions.length) {
        return [selectAllOption, ...selected];
      }

      if (value.some((v) => String(v).includes("All"))) {
        return [selectAllOption, ...customOptions];
      }
      return selected;
    }
    return customOptions.find((opt) => opt.value === String(value));
  };

  const getPlaceholder = () => {
    console.log(api);
    switch (true) {
      case api === "Urgencys":
        return "Select Priority";
      case api === "ClosureCodesTask":
        return "Select close change";
      case api.startsWith("everything/networks?"):
        return "Select Networks";
      case api.startsWith("subtypes?type="):
        return "Select Subtype";
      case api.startsWith("everything/antenna?"):
        return "Select Antennas";
      case api.startsWith("everything/sites?"):
        return "Select Sites";
      case api.startsWith("everything/links?"):
        return "Select Links";
      case api.startsWith("everything/asset?"):
        return "Select Assets";
      case api.startsWith("users/fullnames"):
        return "Select User";
      case api.startsWith("Tasktypes"):
        return "Select Task Type ";
      case api.startsWith("AdminRequestImpact"):
        return "Select Impact ";
      case api.startsWith("type?"):
        return "Select Type ";
      case api === "affected-customers":
        return "Select Customers";
      default:
        return `Select ${api}`;
    }
  };

  // Custom ValueContainer to show max 5 selected values
  // Custom ValueContainer to show max 5 selected values and "+N more"
  const CustomValueContainer = (props: any) => {
    const { getValue, hasValue, children } = props;
    const values = getValue();
    const maxToShow = 6;

    if (isMulti && hasValue && values.length > maxToShow) {
      const selectedValues = children[0].slice(0, maxToShow);

      // Collect hidden labels for tooltip
      const hiddenLabels = values
        .slice(maxToShow)
        .map((v: any) => v.label)
        .join(", ");

      const extraValues = (
        <div
          style={{
            marginLeft: "6px",
            color: "#666",
            cursor: "pointer",
            textDecoration: "underline dotted",
          }}
          title={hiddenLabels} // native browser tooltip
        >
          +{values.length - maxToShow} more
        </div>
      );

      return (
        <components.ValueContainer {...props}>
          {selectedValues}
          {extraValues}
        </components.ValueContainer>
      );
    }

    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  };

  return (
    <div className="api-dropdown-wrapper">
      {label && (
        <label>
          {label} {required && <span style={{ color: "red" }}>*</span>}
        </label>
      )}
      <Select
        isMulti={isMulti}
        options={fullOptions}
        onChange={handleChange}
        value={getValue()}
        placeholder={getPlaceholder()}
        isClearable
        closeMenuOnSelect={!isMulti}
        isDisabled={disabled}
      />
    </div>
  );
}

export default ApiDropdown;
 